import React from 'react'
import TextareaAutosize from 'react-textarea-autosize'
import { isEqual } from 'lodash'
import { isMobile } from 'react-device-detect'
import classNames from 'classnames'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faArrowUp, faSpinner } from '@fortawesome/free-solid-svg-icons'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { createCompletion, fetchLatestThread } from './actions'
import {
  getChannelState,
  isCreating,
  isProcessing,
  getCurrentThread,
  getCurrentThreadKey,
  isFetching,
  getSelectedPersona,
} from './selectors'
import { PilotContextType, ChannelState, File, CreateCompletionParams } from '../../types'
import ChannelStateDot from '../../components/ChannelStateDot'
import PilotContext from 'bundles/Pilot/contexts/PilotContext'
import AttachFileButton from '../files/AttachFileButton'
import { getUploadedFiles, isUploading, getAllFiles } from '../files/selectors'
import { removeAllFiles } from '../files/slice'
import FileUploadThumbnail from '../files/FileUploadThumbnail'
import PillPrompts from '../../components/PillPrompts'

const EMPTY_STRING = ''
const PLACE_HOLDER = 'Ask Solo a question here...'
const ENTER_KEYBOARD_EVENT = 'Enter'

interface PromptSuggestion {
  id: string
  text: string
  context?: any
}

interface Props {
  readonly initialMessage?: string
}

function Footer({ initialMessage }: Props) {
  const dispatch = useAppDispatch()
  const creating = useAppSelector(isCreating, isEqual)
  const processing = useAppSelector(isProcessing, isEqual)
  const fetching = useAppSelector(isFetching, isEqual)
  const channelState = useAppSelector(getChannelState, isEqual)
  const uploading = useAppSelector(isUploading, isEqual)
  const allUploadFiles = useAppSelector(getAllFiles, isEqual)
  const uploadedFiles = useAppSelector(getUploadedFiles, isEqual)
  const currentThread = useAppSelector(getCurrentThread, isEqual)
  const currentThreadKey = useAppSelector(getCurrentThreadKey, isEqual)
  const selectedPersona = useAppSelector(getSelectedPersona, isEqual)
  const {
    agentKey,
    context,
    threadKey: contextThreadKey,
  } = React.useContext<PilotContextType>(PilotContext)

  const [userRequest, setUserRequest] = React.useState(EMPTY_STRING)
  const [loading, setLoading] = React.useState(false)
  const [disabled, setDisabled] = React.useState(true)
  const [submittedInitialMessage, setSubmittedInitialMessage] = React.useState<string | undefined>(
    undefined
  )
  const [showPrompts, setShowPrompts] = React.useState(true)
  const showPromptsRef = React.useRef(showPrompts)

  React.useEffect(() => {
    showPromptsRef.current = showPrompts
  }, [showPrompts])
  const [promptSuggestions, setPromptSuggestions] = React.useState<PromptSuggestion[]>([])
  const [promptsLoading, setPromptsLoading] = React.useState(false)
  const [suggestionsTimestamp, setSuggestionsTimestamp] = React.useState(Date.now())

  const textareaRef = React.useRef(undefined)
  const footerRef = React.useRef<HTMLDivElement>(null)
  const [fetchError, setFetchError] = React.useState(false)

  const buttonClassNames = React.useMemo(
    () =>
      classNames({
        'circle-btn': true,
        'disabled': disabled,
      }),
    [disabled]
  )

  const iconClassNames = React.useMemo(
    () =>
      classNames({
        'fa-spin': loading,
      }),
    [loading]
  )

  React.useEffect(() => {
    const updateFooterHeight = () => {
      if (footerRef.current) {
        const measured = footerRef.current.offsetHeight
        const buffer = 16
        const height = measured + buffer
        document.documentElement.style.setProperty('--footer-height', `${height}px`)
        window.dispatchEvent(new CustomEvent('pilot-footer-height-change', { detail: { height } }))
      }
    }

    // Initial sync
    updateFooterHeight()

    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateFooterHeight)
    })
    if (footerRef.current) {
      resizeObserver.observe(footerRef.current)
    }

    return () => {
      resizeObserver.disconnect()
    }
  }, [allUploadFiles, userRequest, showPrompts])

  React.useEffect(() => {
    if (!currentThread?.id && !fetching && !fetchError) {
      dispatch(fetchLatestThread())
        .unwrap()
        .catch(() => {
          setFetchError(true)
        })
    }
  }, [currentThread?.id, fetching, fetchError, dispatch])

  React.useEffect(() => {
    if (currentThread?.id && fetchError) {
      setFetchError(false)
    }
  }, [currentThread?.id, fetchError])

  const effectiveThreadKey = React.useMemo(
    () => currentThreadKey || contextThreadKey,
    [currentThreadKey, contextThreadKey]
  )
  const isThreadReady = React.useMemo(
    () => currentThread?.id && effectiveThreadKey,
    [currentThread?.id, effectiveThreadKey]
  )

  const submitMessage = React.useCallback(
    (message: string) => {
      if (message.trim() && isThreadReady) {
        const fileIds = uploadedFiles.map((file: File) => file.id)

        const completionParams: CreateCompletionParams = {
          agentKey,
          context,
          userRequest: message,
          fileIds,
          threadKey: effectiveThreadKey,
          pilotThreadId: currentThread.id,
          ...(selectedPersona && { personaId: selectedPersona.id }),
        }

        dispatch(createCompletion(completionParams))
        dispatch(removeAllFiles())

        if (isMobile) {
          textareaRef.current?.blur()
        }

        setShowPrompts(false)
      }
    },
    [
      isThreadReady,
      uploadedFiles,
      agentKey,
      context,
      effectiveThreadKey,
      currentThread?.id,
      dispatch,
    ]
  )

  const handleSubmit = React.useCallback(
    (messageToSubmit?: string) => {
      const message = messageToSubmit || userRequest
      if (message.trim() && !disabled && isThreadReady) {
        submitMessage(message)
        setUserRequest(EMPTY_STRING)
      }
    },
    [userRequest, disabled, isThreadReady, submitMessage]
  )

  const handlePillClick = React.useCallback(
    (prompt: { id: string; text: string; context?: any }) => {
      const pillClickAllowed =
        !creating &&
        !processing &&
        !uploading &&
        ChannelState.Connected === channelState &&
        isThreadReady

      if (pillClickAllowed) {
        const pillClickEvent = new CustomEvent('pill-click', {
          detail: {
            id: prompt.id,
            text: prompt.text,
            context: prompt.context,
          },
          bubbles: true,
        })
        footerRef.current?.dispatchEvent(pillClickEvent)

        submitMessage(prompt.text)
        setUserRequest(EMPTY_STRING)
      }
    },
    [creating, processing, uploading, channelState, isThreadReady, submitMessage]
  )

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (ENTER_KEYBOARD_EVENT === event.key) {
        event.preventDefault()
        handleSubmit()
      }
    },
    [handleSubmit]
  )

  React.useEffect(() => {
    if (!initialMessage) {
      setSubmittedInitialMessage(undefined)
    } else if (
      initialMessage !== submittedInitialMessage &&
      channelState === ChannelState.Connected &&
      isThreadReady
    ) {
      submitMessage(initialMessage)
      setSubmittedInitialMessage(initialMessage)
    }
  }, [initialMessage, submittedInitialMessage, channelState, isThreadReady])

  React.useEffect(() => {
    setLoading(creating || processing)
  }, [creating, processing])

  React.useEffect(() => {
    const newDisabled =
      EMPTY_STRING === userRequest ||
      creating ||
      processing ||
      uploading ||
      ChannelState.Connected !== channelState ||
      !isThreadReady

    setDisabled(newDisabled)
  }, [userRequest, creating, processing, channelState, uploading, isThreadReady])

  React.useEffect(() => {
    const footerElement = footerRef.current

    if (!footerElement) return

    const handleSuggestionsUpdated = (event: CustomEvent) => {
      const { suggestions, loading, timestamp } = event.detail

      const newSuggestions = suggestions || []
      const newLoading = loading || false

      setPromptSuggestions(newSuggestions)
      setPromptsLoading(newLoading)
      setSuggestionsTimestamp(timestamp || Date.now())

      const currentShowPrompts = showPromptsRef.current

      if (newSuggestions.length > 0 && !currentShowPrompts) {
        setShowPrompts(true)
      }

      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('pilot-scroll-to-bottom'))
      })
    }

    const handleSuggestionsLoading = (event: CustomEvent) => {
      const { loading } = event.detail
      setPromptsLoading(loading || false)
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('pilot-scroll-to-bottom'))
      })
    }

    const handleSuggestionsError = (_event: CustomEvent) => {
      setPromptsLoading(false)
      setPromptSuggestions([])
      setSuggestionsTimestamp(Date.now())
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('pilot-scroll-to-bottom'))
      })
    }

    const handleSuggestionsRefreshStart = (event: CustomEvent) => {
      const { loading } = event.detail

      setShowPrompts(true)
      setPromptsLoading(loading || true)
      requestAnimationFrame(() => {
        window.dispatchEvent(new CustomEvent('pilot-scroll-to-bottom'))
      })
    }

    footerElement.addEventListener(
      'prompt-suggestions:updated',
      handleSuggestionsUpdated as EventListener
    )
    footerElement.addEventListener(
      'prompt-suggestions:loading',
      handleSuggestionsLoading as EventListener
    )
    footerElement.addEventListener(
      'prompt-suggestions:error',
      handleSuggestionsError as EventListener
    )
    footerElement.addEventListener(
      'prompt-suggestions:refresh-start',
      handleSuggestionsRefreshStart as EventListener
    )

    return () => {
      footerElement.removeEventListener(
        'prompt-suggestions:updated',
        handleSuggestionsUpdated as EventListener
      )
      footerElement.removeEventListener(
        'prompt-suggestions:loading',
        handleSuggestionsLoading as EventListener
      )
      footerElement.removeEventListener(
        'prompt-suggestions:error',
        handleSuggestionsError as EventListener
      )
      footerElement.removeEventListener(
        'prompt-suggestions:refresh-start',
        handleSuggestionsRefreshStart as EventListener
      )
    }
  }, [])

  const textareaStyle = React.useMemo(() => ({ resize: 'none' as const }), [])

  const handleTextareaChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setUserRequest(e.target.value)
  }, [])

  const stimulusAttributes = React.useMemo(() => {
    const attrs = !isThreadReady
      ? {}
      : {
          'data-controller': 'pilot--prompt-suggestions',
          'data-pilot--prompt-suggestions-agent-key-value': agentKey,
          'data-pilot--prompt-suggestions-pilot-thread-id-value': currentThread?.id,
          'data-pilot--prompt-suggestions-thread-key-value': effectiveThreadKey,
          'data-pilot--prompt-suggestions-user-context-value': JSON.stringify(context || {}),
        }

    return attrs
  }, [isThreadReady, agentKey, currentThread?.id, effectiveThreadKey, context])

  return (
    <div className='pilot-completions-footer' ref={footerRef} {...stimulusAttributes}>
      <div className='pilot-completions-footer__container'>
        <div className='pilot-completions-footer__files'>
          {allUploadFiles.map((file: File) => (
            <FileUploadThumbnail key={file.clientId} file={file} />
          ))}
        </div>
        {showPrompts ? (
          <div className='pilot-completions-footer__prompts'>
            <PillPrompts
              key={`pills-${suggestionsTimestamp}`}
              prompts={promptSuggestions}
              onPillClick={handlePillClick}
              loading={promptsLoading || !isThreadReady}
            />
          </div>
        ) : null}
        <div className='pilot-completions-footer__form'>
          <TextareaAutosize
            className='form-control'
            style={textareaStyle}
            ref={textareaRef}
            rows={1}
            maxRows={2}
            value={userRequest}
            onChange={handleTextareaChange}
            placeholder={PLACE_HOLDER}
            onKeyDown={handleKeyDown}
          />
        </div>
        <div className='pilot-completions-footer__actions'>
          <AttachFileButton threadKey={effectiveThreadKey} disabled={!isThreadReady} />
          <div className='d-flex flex-row align-items-center'>
            <ChannelStateDot channelState={channelState} />
            <div className='ms-3'>
              <button
                type='button'
                className={buttonClassNames}
                onClick={() => handleSubmit()}
                disabled={disabled}
              >
                <FontAwesomeIcon
                  icon={loading ? faSpinner : faArrowUp}
                  className={iconClassNames}
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Footer
